import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatDateTime } from '../utils/dateUtils';
import { FiPlus, FiSettings, FiSave, FiX, FiTrash2, FiCheck, FiClock, FiUser, FiCalendar, FiEye } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { getServicesForDepartment } from '../utils/departmentMapping';

function Reminders() {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [formData, setFormData] = useState({
    guest_id: '',
    type: 'spa',
    outlet: '',
    scheduled_time: '',
    message: ''
  });
  
  const [activitySettings, setActivitySettings] = useState({
    num_reminders: 2,
    interval_type: 'hours',
    interval_value: 1,
    reminder_times: [{ hours: 2, minutes: 0 }, { hours: 1, minutes: 0 }],
    enabled: true
  });

  const filterByDepartment = useCallback((category) => {
    if (user?.role === 'admin' || user?.role === 'manager') return true;
    if (user?.role === 'department' && user?.department) {
      const userServices = getServicesForDepartment(user.department);
      return userServices.includes(category);
    }
    return false;
  }, [user]);

  useEffect(() => {
    fetchReminders();
    fetchSettings();
    fetchSuggestions();
    fetchGuests();
  }, [filterByDepartment]);

  const fetchGuests = async () => {
    try {
      const response = await api.get('/api/admin/guests');
      setGuests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get('/api/alerts/reminders');
      const filtered = (response.data || []).filter(r => filterByDepartment(r.outlet));
      setReminders(filtered);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await api.get('/api/alerts/suggestions');
      const filtered = (response.data.suggestions || []).filter(s => filterByDepartment(s.category));
      setSuggestions(filtered);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/alerts/settings?type=activity_booking');
      if (response.data) {
        setActivitySettings({
          num_reminders: response.data.num_reminders || 2,
          interval_type: response.data.interval_type || 'hours',
          interval_value: response.data.interval_value || 1,
          reminder_times: response.data.reminder_times || [{ hours: 2, minutes: 0 }, { hours: 1, minutes: 0 }],
          enabled: response.data.enabled !== false
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleAcceptSuggestions = async (category = null) => {
    try {
      let bookingIds = [];
      
      if (category) {
        // Accept all for a specific category
        const categorySuggestion = suggestions.find(s => s.category === category);
        if (categorySuggestion) {
          bookingIds = categorySuggestion.bookings.map(b => b.booking_id);
        }
      } else {
        // Accept selected bookings
        bookingIds = Array.from(selectedBookings);
      }

      if (bookingIds.length === 0) {
        alert('Please select at least one booking');
        return;
      }

      const response = await api.post('/api/alerts/suggestions/accept', {
        booking_ids: bookingIds
      });

      alert(`Successfully created ${response.data.created} reminders!`);
      
      // Refresh data
      fetchReminders();
      fetchSuggestions();
      setSelectedBookings(new Set());
    } catch (error) {
      console.error('Failed to accept suggestions:', error);
      alert('Failed to create reminders: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleBooking = (bookingId) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId);
    } else {
      newSelected.add(bookingId);
    }
    setSelectedBookings(newSelected);
  };

  const handleSelectAllCategory = (category) => {
    const categorySuggestion = suggestions.find(s => s.category === category);
    if (categorySuggestion) {
      const allIds = categorySuggestion.bookings.map(b => b.booking_id);
      const newSelected = new Set(selectedBookings);
      allIds.forEach(id => newSelected.add(id));
      setSelectedBookings(newSelected);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/alerts/create', formData);
      setShowModal(false);
      fetchReminders();
      setFormData({
        guest_id: '',
        type: 'spa',
        outlet: '',
        scheduled_time: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to create reminder:', error);
      alert('Failed to create reminder: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await api.put('/api/alerts/settings/activity_booking', activitySettings);
      alert('Reminder settings saved successfully!');
      setShowSettings(false);
      fetchSuggestions(); // Refresh suggestions with new settings
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSettingsLoading(false);
    }
  };

  const addReminderTime = () => {
    setActivitySettings({
      ...activitySettings,
      reminder_times: [...activitySettings.reminder_times, { hours: 1, minutes: 0 }]
    });
  };

  const removeReminderTime = (index) => {
    const newTimes = activitySettings.reminder_times.filter((_, i) => i !== index);
    setActivitySettings({
      ...activitySettings,
      reminder_times: newTimes
    });
  };

  const updateReminderTime = (index, field, value) => {
    const newTimes = [...activitySettings.reminder_times];
    newTimes[index] = { ...newTimes[index], [field]: parseInt(value) || 0 };
    setActivitySettings({
      ...activitySettings,
      reminder_times: newTimes
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  const totalSuggestions = suggestions.reduce((sum, s) => sum + s.count, 0);

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Booking Reminders</h1>
              <p className="text-sm text-gray-500 mt-1">Manage automated and manual reminders for upcoming guest activities.</p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-2">
            {!showSuggestions && totalSuggestions > 0 && (
              <button
                onClick={() => setShowSuggestions(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 font-semibold"
              >
                <FiEye size={20} />
                <span>Show Suggestions ({totalSuggestions})</span>
              </button>
            )}
            <button
              onClick={() => setShowSettings(true)}
                className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center space-x-2 font-semibold"
            >
              <FiSettings size={20} />
              <span>Settings</span>
            </button>
            <button
              onClick={() => setShowModal(true)}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center space-x-2 font-semibold"
            >
              <FiPlus size={20} />
              <span>Create Reminder</span>
            </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total reminders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{reminders.length}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pending</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{reminders.filter(r => r.status === 'pending').length}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Suggestions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalSuggestions}</p>
            </div>
          </div>
        </div>

        {/* Suggestions Section */}
        {showSuggestions && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">Suggested Reminders</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {totalSuggestions > 0 
                      ? `${totalSuggestions} booking${totalSuggestions !== 1 ? 's' : ''} need reminders`
                      : 'No suggestions available'}
                  </p>
                </div>
                <button
                  onClick={() => setShowSuggestions(false)}
                  className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {suggestionsLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading suggestions...</p>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p>No reminder suggestions available. All bookings already have reminders configured.</p>
              </div>
            ) : (
              <div className="p-6">
                {suggestions.map((categoryGroup) => (
                  <div key={categoryGroup.category} className="mb-6 last:mb-0">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{categoryGroup.category}</h3>
                        <p className="text-sm text-gray-500">{categoryGroup.count} booking{categoryGroup.count !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSelectAllCategory(categoryGroup.category)}
                          className="text-sm text-gray-900 hover:text-gray-700 hover:underline"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => handleAcceptSuggestions(categoryGroup.category)}
                          className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center space-x-2 font-semibold"
                        >
                          <FiCheck size={16} />
                          <span>Accept All ({categoryGroup.count})</span>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {categoryGroup.bookings.map((booking) => (
                        <div
                          key={booking.booking_id}
                          className={`border rounded-lg p-4 transition-colors ${
                            selectedBookings.has(booking.booking_id)
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedBookings.has(booking.booking_id)}
                              onChange={() => handleToggleBooking(booking.booking_id)}
                              className="mt-1 w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-300"
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <FiUser className="h-4 w-4 text-gray-400" />
                                <span className="font-medium text-gray-900">{booking.guest_name}</span>
                                {booking.room_no && (
                                  <span className="text-sm text-gray-500">Room {booking.room_no}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <div className="flex items-center space-x-1">
                                  <FiCalendar className="h-4 w-4" />
                                  <span>{booking.date_display}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <FiClock className="h-4 w-4" />
                                  <span>{booking.time_display}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <p className="text-xs text-gray-500 mb-1">Reminders to be created:</p>
                                <div className="flex flex-wrap gap-2">
                                  {booking.reminders.map((reminder, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs"
                                    >
                                      {reminder.hours}h {reminder.minutes}m before
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {selectedBookings.size > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {selectedBookings.size} booking{selectedBookings.size !== 1 ? 's' : ''} selected
                      </span>
                      <button
                        onClick={() => handleAcceptSuggestions()}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 font-semibold"
                      >
                        <FiCheck size={16} />
                        <span>Accept Selected ({selectedBookings.size})</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* All Reminders Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50/70">
            <h2 className="text-lg font-semibold text-gray-900">All Reminders</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outlet/Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scheduled Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reminders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No reminders found
                  </td>
                </tr>
              ) : (
                reminders.map((reminder) => (
                  <tr key={reminder.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {reminder.guest_name || 'Unknown'}
                      </div>
                      {reminder.room_no && (
                        <div className="text-sm text-gray-500">Room {reminder.room_no}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 capitalize">
                        {reminder.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reminder.outlet}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(reminder.scheduled_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          reminder.status === 'sent'
                            ? 'bg-green-100 text-green-800'
                            : reminder.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {reminder.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {reminder.metadata?.auto_generated ? (
                        <span className="text-gray-700">Auto</span>
                      ) : (
                        <span className="text-gray-600">Manual</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create Reminder Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-lg my-8 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create Reminder</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <FiX size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiUser className="inline mr-2" />
                      Guest
                    </label>
                    <select
                      value={formData.guest_id}
                      onChange={(e) =>
                        setFormData({ ...formData, guest_id: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      required
                    >
                      <option value="">Select a guest</option>
                      {guests.map((guest) => (
                        <option key={guest.id} value={guest.id}>
                          {guest.name} {guest.room_no ? `(Room ${guest.room_no})` : ''} - {guest.phone}
                        </option>
                      ))}
                    </select>
                    {guests.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No guests found. Please add guests first.</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reminder Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    >
                      <option value="spa">Spa Appointment</option>
                      <option value="restaurant">Restaurant Reservation</option>
                      <option value="activity">Activity Booking</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Outlet / Service Name
                    </label>
                    <input
                      type="text"
                      value={formData.outlet}
                      onChange={(e) =>
                        setFormData({ ...formData, outlet: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      placeholder="e.g., Main Spa, Restaurant, Yoga Session"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiClock className="inline mr-2" />
                      Scheduled Time (IST)
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_time}
                      onChange={(e) =>
                        setFormData({ ...formData, scheduled_time: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Time is in IST (GMT+5:30)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Message (Optional)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      rows={3}
                      placeholder="Leave empty to use default reminder message"
                    />
                    <p className="text-xs text-gray-500 mt-1">If left empty, a default message will be used based on the reminder type.</p>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm font-semibold"
                  >
                    Create Reminder
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settings Modal - Same as before */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-3xl my-8 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Reminder Settings</h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-800">
                    <strong>Activity Booking Reminders:</strong> Configure automatic reminders for guests with Yoga, Padel, and PickleBall bookings. 
                    Reminders will be automatically generated when bookings are confirmed.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={activitySettings.enabled}
                        onChange={(e) =>
                          setActivitySettings({ ...activitySettings, enabled: e.target.checked })
                        }
                        className="w-4 h-4 text-gray-900 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable Activity Booking Reminders</span>
                    </label>
                  </div>

                  {activitySettings.enabled && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Reminders
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={activitySettings.num_reminders}
                          onChange={(e) => {
                            const num = parseInt(e.target.value) || 1;
                            const currentTimes = activitySettings.reminder_times;
                            let newTimes = [...currentTimes];
                            
                            if (num > currentTimes.length) {
                              for (let i = currentTimes.length; i < num; i++) {
                                newTimes.push({ hours: 1, minutes: 0 });
                              }
                            } else if (num < currentTimes.length) {
                              newTimes = newTimes.slice(0, num);
                            }
                            
                            setActivitySettings({
                              ...activitySettings,
                              num_reminders: num,
                              reminder_times: newTimes
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <p className="text-xs text-gray-500 mt-1">Number of reminders to send per booking (1-10)</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reminder Times (Before Activity)
                        </label>
                        <div className="space-y-2">
                          {activitySettings.reminder_times.map((time, index) => (
                            <div key={index} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-md">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">Reminder {index + 1}</label>
                                <div className="flex items-center space-x-2">
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-1">Hours</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="168"
                                      value={time.hours}
                                      onChange={(e) => updateReminderTime(index, 'hours', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="59"
                                      value={time.minutes}
                                      onChange={(e) => updateReminderTime(index, 'minutes', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                                    />
                                  </div>
                                  {activitySettings.reminder_times.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeReminderTime(index)}
                                      className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-md"
                                    >
                                      <FiTrash2 size={16} />
                                    </button>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Send {time.hours}h {time.minutes}m before the activity
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {activitySettings.reminder_times.length < 10 && (
                          <button
                            type="button"
                            onClick={addReminderTime}
                            className="mt-2 text-sm text-gray-900 hover:text-gray-700 hover:underline"
                          >
                            + Add Another Reminder
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowSettings(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={settingsLoading}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center space-x-2 disabled:opacity-50"
                  >
                    <FiSave size={16} />
                    <span>{settingsLoading ? 'Saving...' : 'Save Settings'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Reminders;
